import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Send, Users } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Guests } from '../lib/api';
import { useApp } from '../context/AppContext';

export default function BulkEmailDialog({ open, onOpenChange, guestIds, onSent }) {
  const { templates, activeShow, guests, refreshGuests } = useApp();
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cc, setCc] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (open) {
      setTemplateId('');
      setSubject('');
      setBody('');
      setCc('');
    }
  }, [open]);

  useEffect(() => {
    const t = templates.find(t => t.id === templateId);
    if (t) {
      setSubject(t.subject);
      setBody(t.body);
    }
  }, [templateId, templates]);

  const recipients = guests.filter(g => guestIds.includes(g.id));
  const sendable = recipients.filter(g => (g.email || '').includes('@'));
  const noEmail = recipients.length - sendable.length;
  const CHUNK = 20;

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      toast({ title: 'Subject and body required' });
      return;
    }
    if (sendable.length === 0) { toast({ title: 'None of the selected guests has an email address' }); return; }
    setSending(true);
    const ccArr = cc.split(',').map(x => x.trim()).filter(Boolean);
    const ids = sendable.map(g => g.id);
    let sent = 0, failed = 0;
    try {
      for (let i = 0; i < ids.length; i += CHUNK) {
        setProgress({ done: i, total: ids.length });
        const r = await Guests.bulkEmail({
          guestIds: ids.slice(i, i + CHUNK), templateId, subjectTemplate: subject, bodyTemplate: body, cc: ccArr
        });
        if (r.reason === 'microsoft_not_connected') {
          toast({ title: 'Microsoft not connected', description: 'Connect Outlook in Settings first.' });
          return;
        }
        sent += r.sent; failed += r.failed;
      }
      toast({ title: `Sent ${sent}/${ids.length}`, description: [failed > 0 && `${failed} failed`, noEmail > 0 && `${noEmail} skipped (no email)`].filter(Boolean).join(' · ') || 'All delivered' });
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Bulk send interrupted', description: `${sent} sent before the error. Re-select the remaining guests and try again.` });
    } finally {
      await refreshGuests();
      setSending(false);
      setProgress(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-4 h-4" /> Send to {sendable.length} guest{sendable.length === 1 ? '' : 's'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase text-stone-500">Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger><SelectValue placeholder="Pick a template (optional)" /></SelectTrigger>
              <SelectContent>
                {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase text-stone-500">Recipients</Label>
            <div className="text-[12px] text-stone-700 max-h-20 overflow-auto p-2 border border-stone-200 rounded" data-testid="bulk-recipients">
              {sendable.map(g => g.email).join(', ')}
            </div>
            {noEmail > 0 && (
              <div className="text-[11.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1" data-testid="bulk-no-email-warning">
                {noEmail} selected guest{noEmail === 1 ? ' has' : 's have'} no email address and will be skipped: {recipients.filter(g => !(g.email || '').includes('@')).slice(0, 6).map(g => g.name).join(', ')}{noEmail > 6 ? '…' : ''}
              </div>
            )}
          </div>
          <div>
            <Label className="text-[11px] uppercase text-stone-500">Cc</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="e.g. producer@..., editor@..." />
          </div>
          <div>
            <Label className="text-[11px] uppercase text-stone-500">Subject (merge tags supported)</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <Label className="text-[11px] uppercase text-stone-500">Body (merge tags supported)</Label>
            <Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} className="font-mono text-[12.5px]" />
            <div className="text-[10.5px] text-stone-500 mt-1">{'{{firstName}} {{episode}} {{showName}} {{releaseLink}} {{appleLink}} {{spotifyLink}} {{youtubeLink}} {{websiteLink}} {{riversideLink}} {{location}} {{supportLink}}'}</div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="bulk-send-btn" onClick={send} disabled={sending || sendable.length === 0} className="text-white" style={{ background: activeShow.color }}>
              <Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? (progress ? `Sending ${progress.done}/${progress.total}…` : 'Sending…') : `Send to ${sendable.length}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
